import { Box, Button, IconButton, Radio, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectCentreOfGravityForm } from "../../../redux/selector";
import { actions } from "../../../redux/slice";
import { CentreOfGravityFormValues, CentreOfGravityStatus } from "../../../types";
import {
  COG_ACTION_BUTTONS,
  COG_ADJUSTMENT_OPTIONS,
  COG_CENTER_METRICS,
  COG_LENGTH_METRICS,
  COG_ROW_KEYS,
  CENTRE_OF_GRAVITY_I18N_PREFIX,
} from "../data";
import { useStyles } from "./styles";

type MetricConfig = {
  field: keyof CentreOfGravityFormValues;
  labelKey: string;
};

const MetricDisplay = ({ labelKey, value }: { labelKey: string; value: string }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  return (
    <Box className={styles.metricField}>
      <Typography className={styles.metricLabel}>{t(labelKey)}</Typography>
      <Typography className={styles.metricValue}>{value}</Typography>
    </Box>
  );
};

const StatusIndicator = ({ status }: { status: CentreOfGravityStatus }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const isOk = status === "OK";
  return (
    <Box className={`${styles.statusBlock} ${isOk ? styles.statusOk : styles.statusNotOk}`}>
      <Typography className={styles.statusLabel}>
        {t(`${CENTRE_OF_GRAVITY_I18N_PREFIX}.STATUS.LABEL`)}
      </Typography>
      <Typography className={styles.statusValue}>
        {t(
          isOk
            ? `${CENTRE_OF_GRAVITY_I18N_PREFIX}.STATUS.OK`
            : `${CENTRE_OF_GRAVITY_I18N_PREFIX}.STATUS.NOT_OK`
        )}
      </Typography>
    </Box>
  );
};

type DataRowProps = {
  titleKey: string;
  metrics: MetricConfig[];
  status: CentreOfGravityStatus;
  showActions?: boolean;
};

const DataRow = ({ titleKey, metrics, status, showActions }: DataRowProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const formValues = useSelector(selectCentreOfGravityForm);
  if (!formValues) return null;

  return (
    <Box className={styles.dataRow}>
      <Typography className={styles.rowTitle}>{t(titleKey)}</Typography>
      <Box className={styles.metricGroup}>
        {metrics.map((metric) => (
          <MetricDisplay
            key={metric.field}
            labelKey={metric.labelKey}
            value={formValues[metric.field]}
          />
        ))}
      </Box>
      <Box className={styles.dataRowEndGroup}>
        {showActions && (
          <Box className={styles.actionButtons}>
            {COG_ACTION_BUTTONS.map((button) => (
              <Button
                key={button.key}
                className={styles.actionBtn}
                variant="outlined"
                color="primary"
              >
                {t(button.labelKey)}
              </Button>
            ))}
          </Box>
        )}
        <StatusIndicator status={status} />
      </Box>
    </Box>
  );
};

const CentreOfGravityPanel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formValues = useSelector(selectCentreOfGravityForm);
  const styles = useStyles();

  if (!formValues) return null;

  return (
    <Box className={styles.panelSection}>
      <DataRow
        titleKey={COG_ROW_KEYS.CENTER_OF_GRAVITY}
        metrics={COG_CENTER_METRICS}
        status={formValues.centerOfGravityStatus}
        showActions
      />
      <Box className={styles.divider} />
      <DataRow
        titleKey={COG_ROW_KEYS.VEHICLE_LENGTH}
        metrics={COG_LENGTH_METRICS}
        status={formValues.vehicleLengthStatus}
      />
      <Box className={styles.divider} />
      <Box className={styles.optionRow}>
        {COG_ADJUSTMENT_OPTIONS.map((option) => (
          <Box
            key={option.value}
            className={`${styles.optionCard} ${
              formValues.adjustmentMode === option.value ? styles.optionCardSelected : ""
            }`}
            onClick={() => dispatch(actions.setCentreOfGravityAdjustmentMode(option.value))}
          >
            <Radio
              size="small"
              checked={formValues.adjustmentMode === option.value}
              onChange={() => dispatch(actions.setCentreOfGravityAdjustmentMode(option.value))}
            />
            <Typography className={styles.optionLabel}>{t(option.labelKey)}</Typography>
          </Box>
        ))}
      </Box>
      <Box className={styles.adjustRow}>
        <IconButton
          className={`${styles.adjustButton} ${styles.adjustButtonPlus}`}
          aria-label={t(`${CENTRE_OF_GRAVITY_I18N_PREFIX}.BUTTONS.INCREASE`)}
        >
          <Typography className={styles.adjustIcon}>+</Typography>
        </IconButton>
        <IconButton
          className={`${styles.adjustButton} ${styles.adjustButtonMinus}`}
          aria-label={t(`${CENTRE_OF_GRAVITY_I18N_PREFIX}.BUTTONS.DECREASE`)}
        >
          <Typography className={styles.adjustIcon}>−</Typography>
        </IconButton>
      </Box>
      <Box className={styles.resetRow}>
        <Button
          className={styles.resetButton}
          variant="outlined"
          onClick={() => dispatch(actions.resetCentreOfGravityForm())}
        >
          {t(`${CENTRE_OF_GRAVITY_I18N_PREFIX}.BUTTONS.RESET_DEFAULTS`)}
        </Button>
      </Box>
    </Box>
  );
};

export default CentreOfGravityPanel;
